/**
 * UAT Persona Registry — Phase 2
 *
 * SECURITY: These are PLAINTEXT dev-only credentials for UAT environments.
 * They are destroyed via `scripts/uat-cleanup.ts` at the end of each UAT cycle.
 * NEVER use these credentials in production. NEVER commit production DB values here.
 *
 * Operator rotates all passwords after first login per PR description instructions.
 */

export interface UatPersona {
  id: string
  platform: string[]
  displayName: string
  email: string
  password: string            // dev-only plaintext; one-time reveal in PR; operator rotates after first login
  role: string
  department?: string
  accessLevel: 'junior' | 'senior' | 'manager' | 'admin' | 'super'
  orgId?: string
  tenantId?: string
  permissionsSummary: string[]
  exampleWorkflows: string[]
  /** Sub-state for roles that have multiple configurations (e.g. loyalty tier) */
  subState?: string
  /** For loyalty app: tier level */
  loyaltyTier?: 'bronze' | 'silver' | 'gold' | 'platinum' | 'black'
  /** For partner portal: OAuth clientId (used instead of email/password) */
  oauthClientId?: string
  /** For loyalty app: phone number used for OTP */
  phone?: string
  /** Platform IAM role key from catalog */
  platformRoleKey?: string
}

// ─── CRM Personas ──────────────────────────────────────────────────────────

export const CRM_PERSONAS: UatPersona[] = [
  {
    id: "crm-super-admin",
    platform: ["crm"],
    displayName: "Sara Al-Othaim (Super Admin)",
    email: "uat-crm-superadmin@othaim-uat.internal",
    password: "UatCrm$upAdmin2026!",
    role: "super_admin",
    department: "Management",
    accessLevel: "super",
    tenantId: "1",
    platformRoleKey: "platform_admin",
    permissionsSummary: [
      "Full CRM access",
      "All leads, pipeline, reports",
      "Agent management and role assignment",
      "Audit log + data export",
      "Discount approvals",
      "SPA agreements + construction",
    ],
    exampleWorkflows: [
      "Review all open leads across all teams",
      "Approve a high-value discount request",
      "Export monthly sales report",
      "Manage agent permissions",
    ],
  },
  {
    id: "crm-admin",
    platform: ["crm", "admin-panel"],
    displayName: "Khalid Bin Nasser (Admin)",
    email: "uat-crm-admin@othaim-uat.internal",
    password: "UatCrmAdm1n2026!",
    role: "admin",
    department: "Management",
    accessLevel: "admin",
    tenantId: "1",
    platformRoleKey: "tenant_admin",
    permissionsSummary: [
      "Full CRM access (isSuperAdmin=true via role=admin)",
      "Admin panel platform operations",
      "All pipeline and reporting",
    ],
    exampleWorkflows: [
      "Manage CRM settings and workflows",
      "Review platform-level configurations in Admin Panel",
      "Generate compliance reports",
    ],
  },
  {
    id: "crm-manager",
    platform: ["crm"],
    displayName: "Noura Al-Dossari (Sales Manager)",
    email: "uat-crm-manager@othaim-uat.internal",
    password: "UatCrmMgr2026!",
    role: "manager",
    department: "Sales",
    accessLevel: "manager",
    tenantId: "1",
    platformRoleKey: "sales_team_lead",
    permissionsSummary: [
      "Full pipeline and reports",
      "Lead distribution and scoring",
      "Invoice + payment plans",
      "Commission management",
      "Campaign management",
      "Service tickets and handovers",
      "Discount approvals",
      "SPA agreements",
    ],
    exampleWorkflows: [
      "Distribute new leads across the team",
      "Review and approve a quotation",
      "Generate a sales performance report",
      "Approve a commission payment",
    ],
  },
  {
    id: "crm-team-lead",
    platform: ["crm"],
    displayName: "Ahmed Al-Rashid (Team Lead)",
    email: "uat-crm-teamlead@othaim-uat.internal",
    password: "UatCrmTL2026!",
    role: "team_lead",
    department: "Sales",
    accessLevel: "senior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard, pipeline, leads, reports",
      "Lead distribution",
      "Agent and team management",
      "Campaigns and campaign contacts",
      "Approvals queue",
      "Customers and quotations",
    ],
    exampleWorkflows: [
      "Reassign a lead to another agent",
      "View team scorecard",
      "Review campaign contacts",
      "Approve a standard quotation",
    ],
  },
  {
    id: "crm-agent",
    platform: ["crm"],
    displayName: "Fatima Al-Harbi (Sales Agent)",
    email: "uat-crm-agent@othaim-uat.internal",
    password: "UatCrmAgt2026!",
    role: "agent",
    department: "Sales",
    accessLevel: "junior",
    tenantId: "1",
    platformRoleKey: "sales_agent",
    permissionsSummary: [
      "Dashboard and pipeline view",
      "Leads management (own leads)",
      "Units browsing",
      "Tasks and activities",
      "Approvals (submit only)",
    ],
    exampleWorkflows: [
      "Create a new lead from inbound enquiry",
      "Update pipeline stage for an opportunity",
      "Schedule a follow-up activity",
      "Submit a quotation for approval",
    ],
  },
  {
    id: "crm-leasing-agent",
    platform: ["crm"],
    displayName: "Omar Bin Saleh (Leasing Agent)",
    email: "uat-crm-leasing@othaim-uat.internal",
    password: "UatCrmLs2026!",
    role: "leasing_agent",
    department: "Sales",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard and pipeline",
      "Leads management (own leads)",
      "Units browsing",
      "Tasks and activities",
    ],
    exampleWorkflows: [
      "Process a rental enquiry",
      "Schedule a unit viewing",
      "Log a follow-up activity",
    ],
  },
  {
    id: "crm-property-manager",
    platform: ["crm"],
    displayName: "Layla Al-Amri (Property Manager)",
    email: "uat-crm-propmanager@othaim-uat.internal",
    password: "UatCrmPM2026!",
    role: "property_manager",
    department: "Operations",
    accessLevel: "senior",
    tenantId: "1",
    permissionsSummary: [
      "Unit management",
      "Pipeline view",
      "Leads (own assignments)",
      "Tasks and activities",
    ],
    exampleWorkflows: [
      "Review units under management",
      "Update unit availability status",
      "Log maintenance request",
    ],
  },
  {
    id: "crm-call-center",
    platform: ["crm"],
    displayName: "Hassan Al-Mutairi (Call Center Agent)",
    email: "uat-crm-callcenter@othaim-uat.internal",
    password: "UatCrmCC2026!",
    role: "call_center",
    department: "Call Center",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard, leads, call-center interface",
      "Campaign contacts",
      "Tasks and activities",
    ],
    exampleWorkflows: [
      "Handle an inbound lead call",
      "Log call outcome",
      "Transfer lead to sales agent",
      "Process campaign contact follow-up",
    ],
  },
  {
    id: "crm-external-broker",
    platform: ["crm"],
    displayName: "Mohammed Al-Zahrani (External Broker)",
    email: "uat-crm-extbroker@othaim-uat.internal",
    password: "UatCrmBrkr2026!",
    role: "external_broker",
    department: "Sales",
    accessLevel: "junior",
    tenantId: "1",
    platformRoleKey: "broker_portal_user",
    permissionsSummary: [
      "Limited pipeline view",
      "Own leads and activities",
      "External access (agent_type=external)",
    ],
    exampleWorkflows: [
      "Submit a referred lead",
      "View commission statement",
      "Update lead status",
    ],
  },
  {
    id: "crm-marketing",
    platform: ["crm"],
    displayName: "Reem Al-Qahtani (Marketing Executive)",
    email: "uat-crm-marketing@othaim-uat.internal",
    password: "UatCrmMkt2026!",
    role: "marketing",
    department: "Marketing",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard view",
      "Leads (read)",
      "Activities log",
    ],
    exampleWorkflows: [
      "Review lead source attribution",
      "Check campaign response rates in CRM",
    ],
  },
  {
    id: "crm-finance",
    platform: ["crm"],
    displayName: "Abdulaziz Al-Otaibi (Finance Analyst)",
    email: "uat-crm-finance@othaim-uat.internal",
    password: "UatCrmFin2026!",
    role: "finance",
    department: "Finance",
    accessLevel: "junior",
    tenantId: "1",
    platformRoleKey: "finance",
    permissionsSummary: [
      "Dashboard view",
      "Invoice and payment plan view",
      "Activities log",
    ],
    exampleWorkflows: [
      "Review outstanding invoices",
      "Check payment plan schedules",
    ],
  },
  {
    id: "crm-legal",
    platform: ["crm"],
    displayName: "Dalal Al-Shehri (Legal Counsel)",
    email: "uat-crm-legal@othaim-uat.internal",
    password: "UatCrmLgl2026!",
    role: "legal",
    department: "Legal",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard view",
      "SPA document review (read)",
      "Activities log",
    ],
    exampleWorkflows: [
      "Review a SPA before signing",
      "Check unit legal status",
    ],
  },
  {
    id: "crm-receptionist",
    platform: ["crm"],
    displayName: "Nadia Al-Harthy (Receptionist)",
    email: "uat-crm-receptionist@othaim-uat.internal",
    password: "UatCrmRcpt2026!",
    role: "receptionist",
    department: "Operations",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard view",
      "Basic lead intake",
      "Activities log",
    ],
    exampleWorkflows: [
      "Log a walk-in visitor enquiry",
      "Direct enquiry to the correct sales agent",
    ],
  },
]

// ─── CX-CRM Personas ────────────────────────────────────────────────────────

export const CX_CRM_PERSONAS: UatPersona[] = [
  {
    id: "cx-platform-super-admin",
    platform: ["cx-crm"],
    displayName: "Platform Admin (CX-CRM)",
    email: "uat-cx-platform@othaim-uat.internal",
    password: "UatCxPlatform2026!",
    role: "platform_super_admin",
    accessLevel: "super",
    tenantId: "1",
    permissionsSummary: [
      "All domains (residential + marketing)",
      "Full permissions in both residential and marketing features",
      "User management across tenants",
    ],
    exampleWorkflows: [
      "Switch between residential and marketing domain views",
      "Manage cross-domain agents",
      "Review unified audit log",
    ],
    subState: "admin_tier=platform_super_admin",
  },
  {
    id: "cx-residential-super-admin",
    platform: ["cx-crm"],
    displayName: "Residential Super Admin (CX-CRM)",
    email: "uat-cx-resi-admin@othaim-uat.internal",
    password: "UatCxResiAdm2026!",
    role: "residential_super_admin",
    accessLevel: "admin",
    tenantId: "1",
    permissionsSummary: [
      "Full residential domain access",
      "Agent management (residential only)",
      "Reports and data export",
    ],
    exampleWorkflows: [
      "Manage residential agents",
      "Review residential pipeline",
      "Generate residential sales report",
    ],
    subState: "admin_tier=residential_super_admin",
  },
  {
    id: "cx-marketing-super-admin",
    platform: ["cx-crm"],
    displayName: "Marketing Super Admin (CX-CRM)",
    email: "uat-cx-mkt-admin@othaim-uat.internal",
    password: "UatCxMktAdm2026!",
    role: "marketing_super_admin",
    accessLevel: "admin",
    tenantId: "1",
    permissionsSummary: [
      "Full marketing domain access",
      "Campaign and automation management",
      "Marketing analytics",
    ],
    exampleWorkflows: [
      "Create and launch a marketing campaign",
      "Review email automation performance",
      "Manage marketing agent permissions",
    ],
    subState: "admin_tier=marketing_super_admin",
  },
  {
    id: "cx-residential-agent",
    platform: ["cx-crm"],
    displayName: "Residential Agent (CX-CRM)",
    email: "uat-cx-resi-agent@othaim-uat.internal",
    password: "UatCxResiAgt2026!",
    role: "residential_agent",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Residential domain features only",
      "Leads, pipeline, activities",
      "Limited reporting",
    ],
    exampleWorkflows: [
      "Process a residential lead",
      "Update pipeline stage",
      "Log a follow-up activity",
    ],
    subState: "admin_tier=residential_agent",
  },
  {
    id: "cx-marketing-agent",
    platform: ["cx-crm"],
    displayName: "Marketing Agent (CX-CRM)",
    email: "uat-cx-mkt-agent@othaim-uat.internal",
    password: "UatCxMktAgt2026!",
    role: "marketing_agent",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Marketing domain features only",
      "Campaign contacts, leads",
      "Marketing analytics",
    ],
    exampleWorkflows: [
      "Work a campaign contact list",
      "Log marketing activity",
      "View campaign performance metrics",
    ],
    subState: "admin_tier=marketing_agent",
  },
]

// ─── Marketing CRM Personas ─────────────────────────────────────────────────

export const MARKETING_CRM_PERSONAS: UatPersona[] = [
  {
    id: "mkt-admin",
    platform: ["marketing-crm"],
    displayName: "Marketing Admin",
    email: "uat-mkt-admin@othaim-uat.internal",
    password: "UatMktAdm2026!",
    role: "admin",
    accessLevel: "admin",
    tenantId: "1",
    platformRoleKey: "marketing_manager",
    permissionsSummary: ["All marketing CRM features"],
    exampleWorkflows: [
      "Manage all campaigns and templates",
      "Configure automation workflows",
      "Access marketing settings",
    ],
  },
  {
    id: "mkt-manager",
    platform: ["marketing-crm"],
    displayName: "Marketing Manager",
    email: "uat-mkt-manager@othaim-uat.internal",
    password: "UatMktMgr2026!",
    role: "manager",
    accessLevel: "manager",
    tenantId: "1",
    platformRoleKey: "marketing_manager",
    permissionsSummary: [
      "Campaigns + email builder + automation + journeys",
      "AI studio + social media",
      "Landing pages + content calendar",
      "Approvals + analytics + settings",
    ],
    exampleWorkflows: [
      "Launch a multi-channel marketing campaign",
      "Build an email automation journey",
      "Review campaign performance analytics",
      "Approve a marketing template",
    ],
  },
  {
    id: "mkt-marketer",
    platform: ["marketing-crm"],
    displayName: "Campaign Marketer",
    email: "uat-mkt-marketer@othaim-uat.internal",
    password: "UatMktMktr2026!",
    role: "marketer",
    accessLevel: "senior",
    tenantId: "1",
    permissionsSummary: [
      "Campaigns, email builder, automation, journeys",
      "AI studio, social media, landing pages",
      "Content calendar, asset library, analytics",
    ],
    exampleWorkflows: [
      "Design a new email campaign",
      "Build a landing page",
      "Schedule social media posts",
      "Analyze campaign results",
    ],
  },
  {
    id: "mkt-reviewer",
    platform: ["marketing-crm"],
    displayName: "Content Reviewer",
    email: "uat-mkt-reviewer@othaim-uat.internal",
    password: "UatMktRvw2026!",
    role: "reviewer",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard + approvals",
      "Analytics view",
      "Content calendar + asset library (read)",
    ],
    exampleWorkflows: [
      "Review and approve a marketing email",
      "Check content calendar for upcoming campaigns",
      "View analytics dashboard",
    ],
  },
  {
    id: "mkt-agent",
    platform: ["marketing-crm"],
    displayName: "Marketing Agent",
    email: "uat-mkt-agent@othaim-uat.internal",
    password: "UatMktAgt2026!",
    role: "agent",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Dashboard, contacts, leads",
      "Campaigns (own), analytics",
    ],
    exampleWorkflows: [
      "Manage contact list for a campaign",
      "Check lead status",
      "View campaign analytics",
    ],
  },
]

// ─── Super Admin Personas ───────────────────────────────────────────────────

export const SUPER_ADMIN_PERSONAS: UatPersona[] = [
  {
    id: "superadmin-super",
    platform: ["super-admin"],
    displayName: "Super Admin (Othaim Park)",
    email: "uat-superadmin-super@othaim-uat.internal",
    password: "UatSASuper2026!",
    role: "super_admin",
    accessLevel: "super",
    tenantId: "1",
    permissionsSummary: ["All super-admin features", "Role management", "Permission matrix administration"],
    exampleWorkflows: [
      "Assign roles to community managers",
      "Review and edit permission matrix",
      "Onboard a new service provider",
    ],
  },
  {
    id: "superadmin-community-manager",
    platform: ["super-admin"],
    displayName: "Community Manager",
    email: "uat-superadmin-commgr@othaim-uat.internal",
    password: "UatSACommMgr2026!",
    role: "community_manager",
    accessLevel: "manager",
    tenantId: "1",
    permissionsSummary: ["Dashboard, residents, events, announcements, complaints, bookings"],
    exampleWorkflows: [
      "Post a community announcement",
      "Manage an event RSVP",
      "Handle a resident complaint",
      "Approve a facility booking",
    ],
  },
  {
    id: "superadmin-fm-manager",
    platform: ["super-admin"],
    displayName: "FM Manager",
    email: "uat-superadmin-fmmgr@othaim-uat.internal",
    password: "UatSAFMMgr2026!",
    role: "fm_manager",
    accessLevel: "manager",
    tenantId: "1",
    permissionsSummary: ["Dashboard, service-catalog, cleaning-maintenance, bookings"],
    exampleWorkflows: [
      "Manage service catalog offerings",
      "Schedule a maintenance task",
      "Confirm a facility booking",
    ],
  },
  {
    id: "superadmin-service-provider",
    platform: ["super-admin"],
    displayName: "Service Provider",
    email: "uat-superadmin-serviceprov@othaim-uat.internal",
    password: "UatSAServProv2026!",
    role: "service_provider",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: ["Dashboard, fnb-orders, laundry, car-services"],
    exampleWorkflows: [
      "View and fulfill an F&B order",
      "Manage laundry pickup schedule",
      "Update car service status",
    ],
  },
  {
    id: "superadmin-finance-manager",
    platform: ["super-admin"],
    displayName: "Finance Manager (Park)",
    email: "uat-superadmin-finmgr@othaim-uat.internal",
    password: "UatSAFinMgr2026!",
    role: "finance_manager",
    accessLevel: "manager",
    tenantId: "1",
    platformRoleKey: "finance",
    permissionsSummary: ["Dashboard, wallets, invoices, refunds"],
    exampleWorkflows: [
      "Review resident wallet balances",
      "Process a refund request",
      "Generate invoice report",
    ],
  },
  {
    id: "superadmin-investor",
    platform: ["super-admin"],
    displayName: "Investor",
    email: "uat-superadmin-investor@othaim-uat.internal",
    password: "UatSAInvestor2026!",
    role: "investor",
    accessLevel: "senior",
    tenantId: "1",
    permissionsSummary: ["Dashboard, investor-portfolio, guest-history"],
    exampleWorkflows: [
      "Review portfolio performance",
      "Check guest occupancy history",
      "View ROI metrics",
    ],
  },
]

// ─── Admin Panel Personas ────────────────────────────────────────────────────

export const ADMIN_PANEL_PERSONAS: UatPersona[] = [
  {
    id: "admin-panel-super",
    platform: ["admin-panel"],
    displayName: "Platform Super Admin (Admin Panel)",
    email: "uat-crm-superadmin@othaim-uat.internal",
    password: "UatCrm$upAdmin2026!",
    role: "super_admin",
    accessLevel: "super",
    tenantId: "1",
    permissionsSummary: [
      "Full admin panel platform operator access via CRM JWT",
      "All tenant configurations",
      "Feature flag and role management",
    ],
    exampleWorkflows: [
      "Toggle a feature flag",
      "Review platform audit log",
      "Manage all tenant configurations",
    ],
  },
  {
    id: "admin-panel-operator",
    platform: ["admin-panel"],
    displayName: "Platform Admin (Admin Panel)",
    email: "uat-crm-admin@othaim-uat.internal",
    password: "UatCrmAdm1n2026!",
    role: "admin",
    accessLevel: "admin",
    tenantId: "1",
    permissionsSummary: [
      "Full admin panel access via CRM JWT",
      "Platform-level configurations",
      "Feature flag management",
    ],
    exampleWorkflows: [
      "Toggle a feature flag",
      "Review platform audit log",
      "Manage tenant configurations",
    ],
  },
  {
    id: "admin-panel-manager",
    platform: ["admin-panel"],
    displayName: "Operations Manager (Admin Panel)",
    email: "uat-crm-manager@othaim-uat.internal",
    password: "UatCrmMgr2026!",
    role: "manager",
    accessLevel: "manager",
    tenantId: "1",
    permissionsSummary: [
      "Admin panel subset: platform reports and configurations",
      "No role management access",
    ],
    exampleWorkflows: [
      "View platform performance reports",
      "Check tenant configuration status",
    ],
  },
  {
    id: "admin-panel-agent",
    platform: ["admin-panel"],
    displayName: "Operations Agent (Admin Panel)",
    email: "uat-crm-agent@othaim-uat.internal",
    password: "UatCrmAgt2026!",
    role: "agent",
    accessLevel: "junior",
    tenantId: "1",
    permissionsSummary: [
      "Limited admin panel access for operational tasks",
      "Read-only platform views",
    ],
    exampleWorkflows: [
      "View operational status dashboard",
      "Check system health indicators",
    ],
  },
]

// ─── O Mall Personas ─────────────────────────────────────────────────────────

export const O_MALL_PERSONAS: UatPersona[] = [
  {
    id: "omall-super-admin",
    platform: ["o-mall"],
    displayName: "O Mall Super Admin",
    email: "uat-omall-superadmin@othaim-uat.internal",
    password: "UatOMallSA2026!",
    role: "super_admin",
    accessLevel: "super",
    permissionsSummary: [
      "Full platform management (admin JWT type)",
      "All mall, retailer, and creator management",
      "Platform-wide reporting and settings",
    ],
    exampleWorkflows: [
      "Manage all mall configurations",
      "Onboard a new retailer",
      "View platform-wide sales report",
    ],
  },
  {
    id: "omall-admin",
    platform: ["o-mall"],
    displayName: "Mall Admin (O Mall)",
    email: "uat-omall-admin@othaim-uat.internal",
    password: "UatOMallAdm2026!",
    role: "admin",
    accessLevel: "admin",
    permissionsSummary: [
      "Mall-level operations (mall_admin JWT type)",
      "Retailer and product management",
      "Order and fulfillment oversight",
    ],
    exampleWorkflows: [
      "Review pending retailer orders",
      "Update mall promotional banner",
      "Generate mall-level revenue report",
    ],
  },
  {
    id: "omall-retailer",
    platform: ["o-mall"],
    displayName: "Retailer (O Mall)",
    email: "uat-omall-retailer@othaim-uat.internal",
    password: "UatOMallRtlr2026!",
    role: "retailer",
    accessLevel: "admin",
    permissionsSummary: [
      "Store management (mall_retailer JWT type)",
      "Product catalog and inventory",
      "Order management for own store",
    ],
    exampleWorkflows: [
      "Add new products to store catalog",
      "Process a customer order",
      "View store sales analytics",
    ],
  },
  {
    id: "omall-creator",
    platform: ["o-mall"],
    displayName: "Creator (O Mall)",
    email: "uat-omall-creator@othaim-uat.internal",
    password: "UatOMallCrtr2026!",
    role: "creator",
    accessLevel: "senior",
    permissionsSummary: [
      "Creator profile + platform_users",
      "Creator commerce surface",
      "Product listings and affiliate links",
    ],
    exampleWorkflows: [
      "Upload a creator product listing",
      "View creator commission earnings",
      "Manage creator storefront",
    ],
  },
  {
    id: "omall-consumer-new",
    platform: ["o-mall"],
    displayName: "New Consumer (O Mall)",
    email: "uat-omall-newcustomer@othaim-uat.internal",
    password: "UatOMallNew2026!",
    role: "consumer",
    accessLevel: "junior",
    permissionsSummary: ["Browse products", "Add to cart", "Checkout", "View orders"],
    exampleWorkflows: [
      "Browse fashion category",
      "Add item to cart and checkout",
      "Apply a loyalty coupon",
      "Track an order",
    ],
    subState: "new_customer_no_purchase_history",
  },
  {
    id: "omall-consumer-repeat",
    platform: ["o-mall"],
    displayName: "Repeat Consumer (O Mall)",
    email: "uat-omall-repeatcustomer@othaim-uat.internal",
    password: "UatOMallRpt2026!",
    role: "consumer",
    accessLevel: "junior",
    permissionsSummary: [
      "Browse products",
      "Cart and checkout with saved addresses",
      "Order history",
      "Wishlist",
      "Loyalty points balance",
    ],
    exampleWorkflows: [
      "Reorder a previous purchase",
      "Browse recommended products",
      "Check loyalty points before checkout",
      "Manage wishlist",
    ],
    subState: "repeat_customer_with_purchase_history",
  },
]

// ─── Buyer Portal Personas ───────────────────────────────────────────────────

export const BUYER_PORTAL_PERSONAS: UatPersona[] = [
  {
    id: "buyer-active",
    platform: ["buyer-portal"],
    displayName: "Active Property Buyer",
    email: "uat-buyer-active@othaim-uat.internal",
    password: "UatBuyerActive2026!",
    role: "buyer",
    accessLevel: "junior",
    tenantId: "1",
    platformRoleKey: "buyer_portal_user",
    permissionsSummary: [
      "Profile management",
      "Document upload and download",
      "Quotation view and acceptance",
      "SPA signing (e-signature)",
      "Payment schedule view",
    ],
    exampleWorkflows: [
      "Accept a property quotation",
      "Upload identity documents",
      "Sign a SPA electronically",
      "View payment plan schedule",
    ],
    subState: "active_buyer_with_quotation",
  },
]

// ─── Loyalty App Personas ────────────────────────────────────────────────────

export const LOYALTY_APP_PERSONAS: UatPersona[] = [
  {
    id: "loyalty-member-bronze",
    platform: ["loyalty-app"],
    displayName: "Bronze Member",
    email: "uat-loyalty-bronze@othaim-uat.internal",
    password: "UatLoyBronze2026!",
    phone: "+966501000001",
    role: "loyalty_member_bronze",
    accessLevel: "junior",
    loyaltyTier: "bronze",
    permissionsSummary: [
      "Earn points (1× rate)",
      "Welcome bonus on join",
      "Birthday gift",
      "Basic redemptions",
    ],
    exampleWorkflows: [
      "View loyalty balance and tier",
      "Earn points from a purchase",
      "Browse available rewards",
      "Redeem a basic reward",
    ],
    subState: "tier=bronze",
  },
  {
    id: "loyalty-member-silver",
    platform: ["loyalty-app"],
    displayName: "Silver Member",
    email: "uat-loyalty-silver@othaim-uat.internal",
    password: "UatLoySilver2026!",
    phone: "+966501000002",
    role: "loyalty_member_silver",
    accessLevel: "junior",
    loyaltyTier: "silver",
    permissionsSummary: [
      "Earn points (1.25× rate)",
      "Early access drops (24h)",
      "Birthday + anniversary bonuses",
      "Quarterly surprise gift",
    ],
    exampleWorkflows: [
      "Access early drop exclusive product",
      "View tier progress toward Gold",
      "Redeem Silver-tier reward",
    ],
    subState: "tier=silver",
  },
  {
    id: "loyalty-member-gold",
    platform: ["loyalty-app"],
    displayName: "Gold Member",
    email: "uat-loyalty-gold@othaim-uat.internal",
    password: "UatLoyGold2026!",
    phone: "+966501000003",
    role: "loyalty_member_gold",
    accessLevel: "senior",
    loyaltyTier: "gold",
    permissionsSummary: [
      "Earn points (1.5× rate)",
      "2h free parking (group-wide)",
      "1 free cinema upgrade per month",
      "Priority customer support",
    ],
    exampleWorkflows: [
      "Use cinema upgrade benefit",
      "View parking benefit QR code",
      "Access priority support channel",
      "Redeem Gold-tier reward",
    ],
    subState: "tier=gold",
  },
  {
    id: "loyalty-member-platinum",
    platform: ["loyalty-app"],
    displayName: "Platinum Member",
    email: "uat-loyalty-platinum@othaim-uat.internal",
    password: "UatLoyPlat2026!",
    phone: "+966501000004",
    role: "loyalty_member_platinum",
    accessLevel: "manager",
    loyaltyTier: "platinum",
    permissionsSummary: [
      "Earn points (2× rate)",
      "Unlimited free parking + valet",
      "Mall lounge access",
      "Dedicated relationship manager",
      "10% partner network discount",
    ],
    exampleWorkflows: [
      "Access mall lounge",
      "Use valet parking benefit",
      "Get 10% partner discount at checkout",
      "Contact dedicated relationship manager",
    ],
    subState: "tier=platinum",
  },
  {
    id: "loyalty-member-black",
    platform: ["loyalty-app"],
    displayName: "Black Member (VIP)",
    email: "uat-loyalty-black@othaim-uat.internal",
    password: "UatLoyBlack2026!",
    phone: "+966501000005",
    role: "loyalty_member_black",
    accessLevel: "admin",
    loyaltyTier: "black",
    permissionsSummary: [
      "Earn points (3× rate)",
      "Personal concierge 24/7",
      "Bespoke private experiences",
      "2:1 charity matching",
      "Invite-only brand events",
    ],
    exampleWorkflows: [
      "Request concierge service",
      "Access invite-only event registration",
      "View bespoke experiences catalog",
      "Activate charity donation matching",
    ],
    subState: "tier=black",
  },
]

// ─── Loyalty Admin Personas ──────────────────────────────────────────────────

export const LOYALTY_ADMIN_PERSONAS: UatPersona[] = [
  {
    id: "loyalty-admin",
    platform: ["loyalty-admin"],
    displayName: "Loyalty Ops Admin",
    email: "uat-loyalty-admin@othaim-uat.internal",
    password: "UatLoyAdmin2026!",
    role: "loyalty_ops",
    accessLevel: "admin",
    tenantId: "1",
    platformRoleKey: "loyalty_ops",
    permissionsSummary: [
      "Member management (search, view, adjust points)",
      "Rules engine configuration",
      "Fraud review queue",
      "Wallet credit issuance",
      "Earning/redemption simulator",
      "Cross-BU transfer management",
      "Tenant loyalty settings",
    ],
    exampleWorkflows: [
      "Manually adjust member points",
      "Review and resolve fraud flag",
      "Create a new earning rule",
      "Simulate a loyalty scenario",
      "Issue wallet credits to a segment",
    ],
  },
]

// ─── Superall Personas ───────────────────────────────────────────────────────

export const SUPERALL_PERSONAS: UatPersona[] = [
  {
    id: "superall-consumer",
    platform: ["superall", "cinema-booking", "fnb-ordering"],
    displayName: "Super App User",
    email: "uat-superall-user@othaim-uat.internal",
    password: "UatSuperallUsr2026!",
    role: "consumer",
    accessLevel: "junior",
    permissionsSummary: [
      "Launcher — access all BU apps",
      "Wallet balance and transfers",
      "Loyalty points view",
      "Cross-BU order history",
      "Cinema booking (authenticated ticket history)",
      "FnB order history",
    ],
    exampleWorkflows: [
      "Launch into O Mall from super app",
      "View unified wallet balance",
      "Check loyalty points",
      "View cinema ticket history",
      "View FnB order history",
    ],
  },
  {
    id: "superall-premium-member",
    platform: ["superall"],
    displayName: "Premium Member (Superall)",
    email: "uat-superall-premium@othaim-uat.internal",
    password: "UatSuperallPrem2026!",
    role: "premium_member",
    accessLevel: "senior",
    permissionsSummary: [
      "All consumer permissions",
      "Premium tier benefits",
      "Early access to new features and promotions",
    ],
    exampleWorkflows: [
      "Access exclusive premium promotions",
      "Early access to new mall openings",
      "View premium member dashboard",
    ],
  },
  {
    id: "superall-family-admin",
    platform: ["superall"],
    displayName: "Family Admin (Superall)",
    email: "uat-superall-familyadmin@othaim-uat.internal",
    password: "UatSuperallFam2026!",
    role: "family_admin",
    accessLevel: "manager",
    permissionsSummary: [
      "All consumer permissions",
      "Family account management",
      "Shared wallet controls and sub-member management",
    ],
    exampleWorkflows: [
      "Add a family member to the shared account",
      "Set spending limits for family sub-members",
      "Review family wallet transaction history",
    ],
  },
  {
    id: "superall-super-admin",
    platform: ["superall"],
    displayName: "Super App Admin",
    email: "uat-superall-admin@othaim-uat.internal",
    password: "UatSuperallAdm2026!",
    role: "super_admin",
    accessLevel: "super",
    permissionsSummary: [
      "All standard user permissions",
      "Tenant approval and management",
      "isSuperAdmin=true",
    ],
    exampleWorkflows: [
      "Approve a new tenant merchant",
      "Manage platform-level configurations",
      "Review cross-tenant audit log",
    ],
    subState: "isSuperAdmin=true",
  },
]

// ─── Partner Portal Personas ─────────────────────────────────────────────────

export const PARTNER_PORTAL_PERSONAS: UatPersona[] = [
  {
    id: "partner-admin",
    platform: ["partner-portal"],
    displayName: "Retail Partner (OAuth)",
    email: "uat-partner-retail@othaim-uat.internal",
    password: "UatPartnerRetail2026!",
    oauthClientId: "uat-retail-partner-001",
    role: "partner_admin",
    accessLevel: "admin",
    platformRoleKey: "partner_admin",
    permissionsSummary: [
      "API dashboard access",
      "API key management",
      "Webhook configuration",
      "Settlement reporting",
      "Lead pipeline (partner-submitted)",
    ],
    exampleWorkflows: [
      "Generate a new API key",
      "Configure a webhook endpoint",
      "View settlement statement",
      "Submit a lead via partner API",
    ],
  },
  {
    id: "partner-brand-operator",
    platform: ["partner-portal"],
    displayName: "Brand Operator",
    email: "uat-partner-brand@othaim-uat.internal",
    password: "UatPartnerBrand2026!",
    oauthClientId: "uat-brand-operator-001",
    role: "brand_operator",
    accessLevel: "senior",
    permissionsSummary: [
      "Brand-level management",
      "Product catalog and promotions management",
      "Brand analytics and reporting",
    ],
    exampleWorkflows: [
      "Update brand product catalog",
      "Create a brand promotion",
      "View brand performance analytics",
    ],
  },
  {
    id: "partner-franchise-owner",
    platform: ["partner-portal"],
    displayName: "Franchise Owner",
    email: "uat-partner-franchise@othaim-uat.internal",
    password: "UatPartnerFranchise2026!",
    oauthClientId: "uat-franchise-owner-001",
    role: "franchise_owner",
    accessLevel: "manager",
    permissionsSummary: [
      "Franchise-level management",
      "Multi-outlet oversight",
      "Cross-outlet reporting and performance tracking",
    ],
    exampleWorkflows: [
      "Review performance across all franchise outlets",
      "Approve an outlet-level promotion",
      "Generate cross-outlet revenue report",
    ],
  },
]

// ─── Cinema Booking Personas ─────────────────────────────────────────────────

export const CINEMA_BOOKING_PERSONAS: UatPersona[] = [
  {
    id: "cinema-consumer-guest",
    platform: ["cinema-booking"],
    displayName: "Cinema Guest (Unauthenticated)",
    email: "uat-cinema-guest@othaim-uat.internal",
    password: "UatCinemaGuest2026!",
    role: "consumer_guest",
    accessLevel: "junior",
    permissionsSummary: [
      "Browse now-showing movies",
      "Select seats and showtime",
      "Pay via Moyasar (card)",
      "Receive booking confirmation + ZATCA invoice",
    ],
    exampleWorkflows: [
      "Browse movies list",
      "Select showtime and seats",
      "Complete Moyasar payment",
      "View booking confirmation",
    ],
    subState: "guest_no_auth",
  },
  {
    id: "cinema-consumer-resident",
    platform: ["cinema-booking", "superall"],
    displayName: "Cinema Member (SSO)",
    email: "uat-cinema-member@othaim-uat.internal",
    password: "UatCinemaMember2026!",
    role: "consumer_resident",
    accessLevel: "junior",
    permissionsSummary: [
      "All guest permissions",
      "Authenticated ticket history (/my/tickets)",
      "Loyalty points earn on booking",
    ],
    exampleWorkflows: [
      "Book a ticket while logged in",
      "View past ticket history",
      "See loyalty points earned from booking",
    ],
    subState: "authenticated_via_superall_sso",
  },
  {
    id: "cinema-admin",
    platform: ["cinema-booking"],
    displayName: "Cinema Admin",
    email: "uat-cinema-admin@othaim-uat.internal",
    password: "UatCinemaAdm2026!",
    role: "cinema_admin",
    accessLevel: "admin",
    permissionsSummary: [
      "Full cinema management",
      "Showtimes, screens, and seat configuration",
      "Cinema-wide reporting",
    ],
    exampleWorkflows: [
      "Add a new showtime",
      "Manage screen and seat layout",
      "View cinema revenue report",
    ],
  },
  {
    id: "cinema-branch-manager",
    platform: ["cinema-booking"],
    displayName: "Cinema Branch Manager",
    email: "uat-cinema-branchmgr@othaim-uat.internal",
    password: "UatCinemaBrMgr2026!",
    role: "branch_manager",
    accessLevel: "manager",
    permissionsSummary: [
      "Single-branch cinema management",
      "Operational reporting for own branch",
      "Staff and shift management",
    ],
    exampleWorkflows: [
      "Review today's showtime schedule",
      "View branch occupancy report",
      "Handle a customer complaint at branch level",
    ],
  },
]

// ─── FnB Ordering Personas ───────────────────────────────────────────────────

export const FNB_ORDERING_PERSONAS: UatPersona[] = [
  {
    id: "fnb-consumer-walkin-qr",
    platform: ["fnb-ordering"],
    displayName: "FnB Guest (QR Dine-in)",
    email: "uat-fnb-guest@othaim-uat.internal",
    password: "UatFnBGuest2026!",
    role: "consumer_walkin_qr",
    accessLevel: "junior",
    permissionsSummary: [
      "No auth; scan QR and browse dine-in menu",
      "Add to cart and pay via Moyasar",
      "View order status + ZATCA invoice",
    ],
    exampleWorkflows: [
      "Scan QR code and browse dine-in menu",
      "Add items and checkout",
      "Track live order status",
      "View ZATCA invoice",
    ],
    subState: "guest_dine_in_qr",
  },
  {
    id: "fnb-consumer-pickup",
    platform: ["fnb-ordering"],
    displayName: "FnB Pickup Customer",
    email: "uat-fnb-pickup@othaim-uat.internal",
    password: "UatFnBPickup2026!",
    role: "consumer_pickup",
    accessLevel: "junior",
    permissionsSummary: [
      "No auth; pickup order mode",
      "Browse menu and place pickup order",
      "Pay via Moyasar",
    ],
    exampleWorkflows: [
      "Browse menu and select pickup option",
      "Place order and receive pickup time",
      "Pay and collect order",
    ],
    subState: "guest_pickup_order",
  },
  {
    id: "fnb-consumer-delivery",
    platform: ["fnb-ordering", "superall"],
    displayName: "FnB Delivery Member (SSO)",
    email: "uat-fnb-member@othaim-uat.internal",
    password: "UatFnBMember2026!",
    role: "consumer_delivery",
    accessLevel: "junior",
    permissionsSummary: [
      "Authenticated via Superall SSO",
      "Delivery order mode with saved addresses",
      "Authenticated order history (/my/orders)",
      "Loyalty points earn on orders",
    ],
    exampleWorkflows: [
      "Place delivery order while logged in",
      "Track delivery in real-time",
      "View full order history",
      "See loyalty points earned from order",
    ],
    subState: "authenticated_via_superall_sso",
  },
  {
    id: "fnb-operator",
    platform: ["fnb-ordering"],
    displayName: "FnB Operator",
    email: "uat-fnb-operator@othaim-uat.internal",
    password: "UatFnBOpr2026!",
    role: "fnb_operator",
    accessLevel: "senior",
    permissionsSummary: [
      "Kitchen queue management",
      "Order status updates",
      "Menu management for own outlet",
    ],
    exampleWorkflows: [
      "Accept and update order in kitchen queue",
      "Mark order as ready for pickup",
      "Update menu item availability",
    ],
  },
  {
    id: "fnb-admin",
    platform: ["fnb-ordering"],
    displayName: "FnB Admin",
    email: "uat-fnb-admin@othaim-uat.internal",
    password: "UatFnBAdm2026!",
    role: "fnb_admin",
    accessLevel: "admin",
    permissionsSummary: [
      "Full F&B management",
      "Menus, outlets, and staff configuration",
      "F&B-wide reporting and analytics",
    ],
    exampleWorkflows: [
      "Add a new FnB outlet",
      "Configure outlet menus and pricing",
      "View cross-outlet F&B revenue report",
    ],
  },
]

// ─── Othaim Park Personas ─────────────────────────────────────────────────────
// Source: artifacts/othaim-park/src/pages/BuyerPortal.tsx (BuyerUser type)
//         artifacts/othaim-park/src/pages/broker/BrokerLogin.tsx (BrokerUser.status)

export const OTHAIM_PARK_PERSONAS: UatPersona[] = [
  {
    id: "park-buyer",
    platform: ["othaim-park"],
    displayName: "Turki Al-Sabaan (Buyer)",
    email: "uat-park-buyer@othaim-uat.internal",
    password: "UatParkBuyr2026!",
    role: "buyer",
    department: "Residential Buyers",
    accessLevel: "junior",
    permissionsSummary: [
      "View own unit, payment plan, and construction progress",
      "Upload KYC documents for reservation confirmation",
      "View handover timeline and snag list",
      "Message assigned sales agent",
    ],
    exampleWorkflows: [
      "Review the payment schedule and confirm next installment due date",
      "Upload bank finance approval to complete KYC",
      "Check construction progress photos for Unit P-0506",
    ],
  },
  {
    id: "park-broker-active",
    platform: ["othaim-park"],
    displayName: "Majd Al-Khalidi (Active Broker)",
    email: "uat-park-broker-active@othaim-uat.internal",
    password: "UatParkBrkr2026!",
    role: "broker_active",
    department: "Channel Partners",
    accessLevel: "senior",
    permissionsSummary: [
      "Active RERA-licensed broker with full portal access",
      "View assigned leads and pipeline status",
      "Download commission statements",
    ],
    exampleWorkflows: [
      "Check status of 5 submitted buyer leads",
      "Download Q2 commission statement for 3 closed deals",
    ],
    subState: "status=active",
  },
  {
    id: "park-broker-pending",
    platform: ["othaim-park"],
    displayName: "Dana Al-Rashidi (Pending Broker)",
    email: "uat-park-broker-pending@othaim-uat.internal",
    password: "UatParkBrkrPnd!6",
    role: "broker_pending_approval",
    department: "Channel Partners",
    accessLevel: "junior",
    permissionsSummary: [
      "Pending-approval broker — limited portal view",
      "View own profile and current approval status",
      "Cannot access leads or commissions until approved",
    ],
    exampleWorkflows: [
      "Check approval status and see which documents are still required",
      "Update RERA license number in the broker profile",
    ],
    subState: "status=pending_approval",
  },
]

// ─── O Residence Web-App Personas ─────────────────────────────────────────────
// Source: artifacts/o-residence/web-app/src/lib/auth.tsx (ResidentProfile type)
//         artifacts/o-residence/web-app/src/App.tsx (routes)

export const O_RESIDENCE_PERSONAS: UatPersona[] = [
  {
    id: "ores-resident-owner",
    platform: ["o-residence"],
    displayName: "Fatima Al-Dosari (Resident Owner)",
    email: "uat-ores-resident@othaim-uat.internal",
    password: "UatOResRsdt2026!",
    role: "resident_owner",
    department: "O Residence Residents",
    accessLevel: "junior",
    permissionsSummary: [
      "Submit and track maintenance requests for own unit",
      "Post to community board, vote in polls, view announcements",
      "Track handover milestones and snag list status",
      "Manage notification preferences and resident profile",
    ],
    exampleWorkflows: [
      "Submit a maintenance request for a leaking tap in Unit 0807",
      "Post a community event proposal to the community board",
      "Check the handover checklist and confirm items already received",
    ],
  },
]

// ─── Master export ───────────────────────────────────────────────────────────

export const ALL_PERSONAS: UatPersona[] = [
  ...CRM_PERSONAS,
  ...CX_CRM_PERSONAS,
  ...MARKETING_CRM_PERSONAS,
  ...SUPER_ADMIN_PERSONAS,
  ...ADMIN_PANEL_PERSONAS,
  ...O_MALL_PERSONAS,
  ...BUYER_PORTAL_PERSONAS,
  ...LOYALTY_APP_PERSONAS,
  ...LOYALTY_ADMIN_PERSONAS,
  ...SUPERALL_PERSONAS,
  ...PARTNER_PORTAL_PERSONAS,
  ...CINEMA_BOOKING_PERSONAS,
  ...FNB_ORDERING_PERSONAS,
  ...OTHAIM_PARK_PERSONAS,
  ...O_RESIDENCE_PERSONAS,
]

/** Lookup by persona ID — O(1) */
export const PERSONA_BY_ID: Record<string, UatPersona> = Object.fromEntries(
  ALL_PERSONAS.map(p => [p.id, p])
)

/** All personas for a given platform slug */
export function personasForPlatform(platform: string): UatPersona[] {
  return ALL_PERSONAS.filter(p => p.platform.includes(platform))
}

/** Group personas by their role for the dropdown UI */
export function personasByRole(): Record<string, UatPersona[]> {
  const groups: Record<string, UatPersona[]> = {}
  for (const p of ALL_PERSONAS) {
    if (!groups[p.role]) groups[p.role] = []
    groups[p.role].push(p)
  }
  return groups
}
